// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';

/**
 * Advisor (read-only) access to a profile's living, versioned profile forms
 * (fact-find, risk attitude). The advisor views the latest submitted version on
 * the relationship page; clients own the editing. Mirrors the Resource Server's
 * /api/profiles/{profileId}/forms/{formType}/** surface (proxied by advisor-bff).
 * See docs/specs/repeatable-profile-forms-prd.md.
 */

export type ProfileFormType = 'FACT_FIND' | 'RISK_ATTITUDE' | 'SUSTAINABILITY' | 'DEEP_DIVE';

export type FactFindFieldType =
  | 'text' | 'string' | 'integer' | 'number' | 'decimal'
  | 'currency' | 'boolean' | 'date' | 'enum' | 'array'
  // Schema DSL v2 (Fact Find wizard)
  | 'percent' | 'money' | 'term' | 'phone' | 'address' | 'clientRef' | 'document';

export interface FactFindOption {
  value: string;
  label?: string;
  points?: number;
  /** DSL v2: field ids revealed when this option is selected. */
  followUps?: string[];
}

export interface FactFindQuestion {
  id: string;
  label?: string;
  type: FactFindFieldType;
  section?: string;
  options?: Array<string | FactFindOption>;
  /** Deep Dive metadata: topic grouping for the read-only tab. */
  topic?: string;
  /** Deep Dive metadata: free-text relevance hint (conversational gating). */
  askWhen?: string;
}

/** DSL v2 gate (boolean Yes/No or multiselect with reveal options). */
export interface WizardGate {
  id: string;
  type: 'boolean' | 'multiselect';
  label: string;
  options?: Array<{ value: string; label?: string; exclusive?: boolean }>;
}

export interface WizardGroup {
  id: string;
  title?: string;
  repeating: boolean;
  gate?: WizardGate;
  fields: FactFindQuestion[];
}

export interface WizardSection {
  id: string;
  title: string;
  kind?: 'FORM' | 'CHAT';
  layout?: 'SHARED' | 'PER_CLIENT';
  gate?: WizardGate;
  fields?: FactFindQuestion[];
  groups?: WizardGroup[];
}

export interface FactFindSchema {
  dslVersion?: number;
  version?: string | number;
  title?: string;
  description?: string;
  /** DSL v1: { id, title } refs; DSL v2: full wizard sections. */
  sections?: WizardSection[];
  /** Present on DSL v1 (flat) schemas only. */
  questions?: FactFindQuestion[];
}

export interface RiskFlag {
  key: string;
  severity: 'ok' | 'info' | 'warn' | 'alert' | string;
  message: string;
}

export interface RiskResult {
  level: number;
  raw: number;
  flags: RiskFlag[];
}

/** One row of a sustainability preference profile. */
export interface SustainabilityProfileRow {
  label: string;
  value: string;
}

/** Machine-readable sustainability preferences (drive fund matching / recommendations). */
export interface SustainabilityPreferences {
  hasPreferences: boolean;
  taxonomyMinBand: number;
  sfdrMinBand: number;
  paiConsidered: boolean;
  paiAreas: string[];
  paiEvidence?: string | null;
  exclusions: string[];
}

/** The computed outcome of a SUSTAINABILITY version (preference profile + flags; no score). */
export interface SustainabilityResult {
  preferences: SustainabilityPreferences;
  profile: SustainabilityProfileRow[];
  flags: RiskFlag[];
  stringency: number;
}

export interface ProfileFormVersion {
  id: string;
  recordId: string;
  formType: string;
  versionNo: number;
  schemaVersion: number;
  answers: Record<string, unknown>;
  result?: RiskResult | SustainabilityResult | null;
  source?: 'CHATBOT' | 'FORM' | null;
  submittedAt?: string;
  /** Storage path of the sealed assessment PDF archived for this version (V267). */
  documentStoragePath?: string | null;
}

export type SectionStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE';

/** The wizard's working state — mirrors the RS ProfileFormDraftDTO. */
export interface ProfileFormDraft {
  formType: string;
  answers: Record<string, unknown>;
  sectionStatus: Record<string, SectionStatus>;
  status: SectionStatus;
  schemaVersion: number;
  /** Set when a save appended an immutable version (document COMPLETE). */
  appendedVersionNo?: number | null;
  updatedAt?: string;
}

export const profileFormApi = {
  /** Effective schema (for rendering answer labels). */
  getSchema: async (profileId: string, formType: ProfileFormType): Promise<FactFindSchema> => {
    const { data } = await api.get<FactFindSchema>(`/profiles/${profileId}/forms/${formType}/schema`);
    return data;
  },

  /**
   * Latest submitted version, or null if never submitted (204). PER_CLIENT
   * types (risk / sustainability / deep dive) require the subject clientId;
   * the household fact-find takes none.
   */
  getLatest: async (
    profileId: string,
    formType: ProfileFormType,
    clientId?: string,
  ): Promise<ProfileFormVersion | null> => {
    const { data, status } = await api.get<ProfileFormVersion | ''>(
      `/profiles/${profileId}/forms/${formType}/latest`,
      { params: clientId ? { clientId } : undefined, validateStatus: (s) => s === 200 || s === 204 },
    );
    return status === 204 || data === '' ? null : (data as ProfileFormVersion);
  },

  /** Full version history, newest first (per subject client for PER_CLIENT types). */
  listVersions: async (
    profileId: string,
    formType: ProfileFormType,
    clientId?: string,
  ): Promise<ProfileFormVersion[]> => {
    const { data } = await api.get<ProfileFormVersion[]>(
      `/profiles/${profileId}/forms/${formType}/versions`,
      { params: clientId ? { clientId } : undefined },
    );
    return data;
  },

  /**
   * The editable wizard draft (advisor edits the assigned relationship's form;
   * shares the one live draft with the client). 409 when the effective schema
   * is DSL v1 — callers fall back to the read-only view.
   */
  getDraft: async (profileId: string, formType: ProfileFormType): Promise<ProfileFormDraft> => {
    const { data } = await api.get<ProfileFormDraft>(`/profiles/${profileId}/forms/${formType}/draft`);
    return data;
  },

  /** Validate + save one section into the draft; returns the updated draft. */
  saveSection: async (
    profileId: string,
    formType: ProfileFormType,
    sectionId: string,
    sectionAnswers: Record<string, unknown>,
  ): Promise<ProfileFormDraft> => {
    const { data } = await api.put<ProfileFormDraft>(
      `/profiles/${profileId}/forms/${formType}/sections/${sectionId}`,
      sectionAnswers,
    );
    return data;
  },
};
